import api from './client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// API Constants
export const QUERY_KEYS = {
  employees: () => ['employees'],
  employee: (id: number) => ['employee', id],
  devices: () => ['devices'],
  device: (id: number) => ['device', id],
  timesheets: () => ['timesheets'],
  timesheet: (id: number) => ['timesheet', id],
  penalties: () => ['penalties'],
  dashboard: () => ['dashboard'],
  shifts: () => ['shifts'],
  branches: () => ['branches'],
  departments: () => ['departments'],
  holidays: () => ['holidays'],
  shiftAssignments: () => ['shift-assignments'],
  shiftChangeRequests: () => ['shift-change-requests'],
  attendanceEvents: () => ['attendance-events'],
  penaltyRules: () => ['penalty-rules'],
  users: () => ['users'],
  auditLogs: () => ['audit-logs'],
  roles: () => ['roles'],
  organizations: () => ['organizations'],
  operationsWeeklyPlan: () => ['operations-weekly-plan'],
  operationsActiveStaff: () => ['operations-active-staff'],
  employeeDayOffs: () => ['employee-day-offs'],
  employeeDayOffPatterns: () => ['employee-day-off-patterns'],
  penaltyPresets: () => ['penalty-presets'],
  kpiSummary: () => ['kpi-summary'],
  // Payroll keys
  payrollRuns: () => ['payroll-runs'],
  payrollRun: (id: number) => ['payroll-run', id],
  payrollProfiles: () => ['payroll-profiles'],
  payrollProfile: (id: number) => ['payroll-profile', id],
  salaryComponents: () => ['salary-components'],
  leaveRequests: () => ['leave-requests'],
  leaveRequest: (id: number) => ['leave-request', id],
  leaveTypes: () => ['leave-types'],
  payrollRulesets: () => ['payroll-rulesets'],
  payslips: (runId: number) => ['payslips', runId],
  payslip: (id: number) => ['payslip', id],
  payrollAdjustments: () => ['payroll-adjustments'],
};

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/user'),
};

// Profile API - User's own profile management
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data: { name?: string; telegram_id?: string | null; current_password?: string; new_password?: string; new_password_confirmation?: string }) =>
    api.patch('/profile', data),
  updateNotifications: (data: Record<string, boolean>) =>
    api.patch('/profile/notifications', data),
  unlinkTelegram: () => api.delete('/profile/telegram'),
};

// Employees API
export const employeesAPI = {
  list: ({ page = 1, per_page = 20, search = '', branch_id = '', department_id = '', is_active = '', include_inactive = false } = {}) =>
    api.get('/employees', { params: { page, per_page, search, branch_id, department_id, is_active, include_inactive } }),
  get: (id: number) => api.get(`/employees/${id}`),
  export: ({ search = '', branch_id = '', department_id = '', is_active = '', include_inactive = true } = {}) =>
    api.get('/exports/employees', { params: { search, branch_id, department_id, is_active, include_inactive }, responseType: 'blob' }),
  create: (data: any) => {
    if (data instanceof FormData) {
      return api.post('/employees', data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.post('/employees', data);
  },
  update: (id: number, data: any) => {
    if (data instanceof FormData) {
      data.append('_method', 'PATCH');
      return api.post(`/employees/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    return api.patch(`/employees/${id}`, data);
  },
  delete: (id: number) => api.delete(`/employees/${id}`),
  bindDevice: (id: number, data: any) => api.post(`/employees/${id}/bind-device`, data),
  unbindDevice: (id: number, bindingId: number) =>
    api.delete(`/employees/${id}/unbind-device/${bindingId}`),
};

// Devices API
export const devicesAPI = {
  list: ({ page = 1, per_page = 20, branch_id = '', status = '', search = '' } = {}) =>
    api.get('/devices', { params: { page, per_page, branch_id, status, search } }),
  get: (id: number) => api.get(`/devices/${id}`),
  create: (data: any) => api.post('/devices', data),
  update: (id: number, data: any) => api.patch(`/devices/${id}`, data),
  delete: (id: number) => api.delete(`/devices/${id}`),
  testConnection: (id: number) => api.post(`/devices/${id}/test-connection`),
  getHikvisionConnection: (id: number) => api.get(`/devices/${id}/hikvision/connection`),
  syncHikvision: (id: number, data?: { from?: string; to?: string; page_size?: number }) =>
    api.post(`/devices/${id}/hikvision/sync`, data),
  rotateSecret: (id: number) => api.post(`/devices/${id}/rotate-secret`),
  getEmployees: (id: number) => api.get(`/devices/${id}/employees`),
  addIpAllowlist: (id: number, data: any) =>
    api.post(`/devices/${id}/ip-allowlist`, data),
  removeIpAllowlist: (id: number, allowlistId: number) =>
    api.delete(`/devices/${id}/ip-allowlist/${allowlistId}`),
};

// Timesheets API
export const timesheetsAPI = {
  list: ({ page = 1, per_page = 50, employee_id = '', branch_id = '', date_from = '', date_to = '' } = {}) =>
    api.get('/timesheets', { params: { page, per_page, employee_id, branch_id, date_from, date_to } }),
  get: (id: number) => api.get(`/timesheets/${id}`),
  update: (id: number, data: any) => api.patch(`/timesheets/${id}`, data),
  manualAttendance: (id: number, data: { check_in_at?: string; check_out_at?: string; reason: string }) =>
    api.post(`/timesheets/${id}/manual-attendance`, data),
  recompute: (data: any) => api.post('/timesheets/recompute', data),
  // Kelish ruxsati (arrival excuse)
  setExcuse: (id: number, data: { excused_until: string; excuse_reason: string }) =>
    api.post(`/timesheets/${id}/excuse`, data),
  removeExcuse: (id: number) => api.delete(`/timesheets/${id}/excuse`),
  // Erta ketish ruxsati (early leave excuse)
  setEarlyLeaveExcuse: (id: number, data: { early_leave_excused_until: string; early_leave_excuse_reason: string }) =>
    api.post(`/timesheets/${id}/early-leave-excuse`, data),
  removeEarlyLeaveExcuse: (id: number) => api.delete(`/timesheets/${id}/early-leave-excuse`),
  monthlySummary: ({ employee_id = '', month = '' } = {}) => {
    const isYearMonth = typeof month === 'string' && /^\d{4}-\d{2}$/.test(month);
    const [year = '', monthNumber = ''] = isYearMonth ? month.split('-') : ['', ''];

    return api.get('/timesheets/monthly-summary', {
      params: {
        employee_id,
        year,
        month: isYearMonth ? monthNumber : month,
      },
    });
  },
};

// Penalties API
export const penaltiesAPI = {
  list: ({ page = 1, per_page = 20, employee_id = '', branch_id = '', status = '', penalty_type = '', date_from = '', date_to = '' } = {}) =>
    api.get('/penalties', { params: { page, per_page, employee_id, branch_id, status, penalty_type, date_from, date_to } }),
  export: ({ employee_id = '', branch_id = '', status = '', date_from = '', date_to = '' } = {}) =>
    api.get('/penalties/export', { params: { employee_id, branch_id, status, date_from, date_to }, responseType: 'blob' }),
  create: (data: any) => api.post('/penalties', data),
  applyChecklist: (data: {
    employee_id: number;
    work_date: string;
    preset_ids: number[];
    preset_amounts?: Record<number, number>;
    notes?: string;
  }) => api.post('/penalties/apply-checklist', data),
  approve: (id: number) => api.patch(`/penalties/${id}`, { status: 'approved' }),
  override: (id: number, data: any) => api.post(`/penalties/${id}/override`, data),
  refund: (id: number, data: { reason: string }) => api.post(`/penalties/${id}/refund`, data),
  reject: (id: number) => api.post(`/penalties/${id}/reject`),
};

// Operations API
export const operationsAPI = {
  weeklyPlan: ({ week_start_date, branch_id = '' }: { week_start_date: string; branch_id?: string | number }) =>
    api.get('/operations/weekly-plan', { params: { week_start_date, branch_id } }),
  saveWeeklyPlan: (data: {
    entries: Array<{ employee_id: number; date: string; shift_id?: number | null; is_day_off?: boolean; reason?: string }>;
  }) => api.post('/operations/weekly-plan', data),
  activeStaff: ({ branch_id = '', at = '' } = {}) =>
    api.get('/operations/active-staff', { params: { branch_id, at } }),
};

// Employee Day Offs API
export const employeeDayOffsAPI = {
  list: ({ employee_id = '', date_from = '', date_to = '' } = {}) =>
    api.get('/employee-day-offs', { params: { employee_id, date_from, date_to } }),
  create: (data: { employee_id: number; off_date: string; reason?: string; is_paid?: boolean }) =>
    api.post('/employee-day-offs', data),
  update: (id: number, data: { off_date?: string; reason?: string; is_paid?: boolean }) =>
    api.patch(`/employee-day-offs/${id}`, data),
  delete: (id: number) => api.delete(`/employee-day-offs/${id}`),
};

export const employeeDayOffPatternsAPI = {
  list: ({ employee_id = '', is_active = '' } = {}) =>
    api.get('/employee-day-off-patterns', { params: { employee_id, is_active } }),
  create: (data: {
    employee_id: number;
    weekdays: number[];
    reason: string;
    is_paid?: boolean;
    effective_from?: string | null;
    is_active?: boolean;
  }) => api.post('/employee-day-off-patterns', data),
  update: (id: number, data: {
    weekdays?: number[];
    reason?: string;
    is_paid?: boolean;
    effective_from?: string | null;
    is_active?: boolean;
  }) => api.patch(`/employee-day-off-patterns/${id}`, data),
  delete: (id: number) => api.delete(`/employee-day-off-patterns/${id}`),
};

// Penalty Presets API
export const penaltyPresetsAPI = {
  list: ({ branch_id = '', is_active = '' } = {}) =>
    api.get('/penalty-presets', { params: { branch_id, is_active } }),
  create: (data: any) => api.post('/penalty-presets', data),
  update: (id: number, data: any) => api.patch(`/penalty-presets/${id}`, data),
  delete: (id: number) => api.delete(`/penalty-presets/${id}`),
};

// KPI API
export const kpiAPI = {
  importSales: (data: { records: any[]; default_bonus_rate?: number }) =>
    api.post('/kpi/sales/import', data),
  summary: ({ date_from, date_to, branch_id = '' }: { date_from: string; date_to: string; branch_id?: string | number }) =>
    api.get('/kpi/summary', { params: { date_from, date_to, branch_id } }),
};

// Dashboard API
export const dashboardAPI = {
  get: ({ branch_id = '' } = {}) =>
    api.get('/dashboard', { params: { branch_id } }),
};

// Shifts API
export const shiftsAPI = {
  list: ({ branch_id = '', page = 1 } = {}) =>
    api.get('/shifts', { params: { branch_id, page } }),
  get: (id: number) => api.get(`/shifts/${id}`),
  create: (data: any) => api.post('/shifts', data),
  update: (id: number, data: any) => api.patch(`/shifts/${id}`, data),
  delete: (id: number) => api.delete(`/shifts/${id}`),
};

// Reports API
export const reportsAPI = {
  daily: ({ date = '', branch_id = '' } = {}) =>
    api.get('/reports/daily', { params: { date, branch_id } }),
  monthly: ({ month = '', branch_id = '' } = {}) =>
    api.get('/reports/monthly', { params: { month, branch_id } }),
  payroll: ({ month = '', branch_id = '' } = {}) =>
    api.get('/reports/payroll', { params: { month, branch_id } }),
  export: ({ type = 'monthly', month = '', date = '', branch_id = '' } = {}): Promise<Blob> =>
    api.get<Blob>('/reports/export', { params: { type, month, date, branch_id }, responseType: 'blob' }),
};

// Departments API
export const departmentsAPI = {
  list: ({ branch_id = '', page = 1, per_page = 20 } = {}) =>
    api.get('/departments', { params: { branch_id, page, per_page } }),
  get: (id: number) => api.get(`/departments/${id}`),
  create: (data: any) => api.post('/departments', data),
  update: (id: number, data: any) => api.patch(`/departments/${id}`, data),
  delete: (id: number) => api.delete(`/departments/${id}`),
};

// Branches API
export const branchesAPI = {
  // per_page defaults to '' on purpose: BranchController only paginates when the param is
  // present, so dropdowns get the full list instead of being silently capped at 20.
  list: ({ page = 1, per_page = '', search = '' }: { page?: number; per_page?: number | string; search?: string } = {}) =>
    api.get('/branches', { params: { page, per_page, search } }),
  get: (id: number) => api.get(`/branches/${id}`),
  create: (data: any) => api.post('/branches', data),
  update: (id: number, data: any) => api.patch(`/branches/${id}`, data),
  delete: (id: number) => api.delete(`/branches/${id}`),
};

// Holidays API
export const holidaysAPI = {
  list: ({ branch_id = '', year = '' } = {}) =>
    api.get('/holidays', { params: { branch_id, year } }),
  create: (data: any) => api.post('/holidays', data),
  update: (id: number, data: any) => api.patch(`/holidays/${id}`, data),
  delete: (id: number) => api.delete(`/holidays/${id}`),
};

// Shift Assignments API
export const shiftAssignmentsAPI = {
  list: ({ employee_id = '', department_id = '', from_date = '', to_date = '', page = 1 } = {}) =>
    api.get('/shift-assignments', { params: { employee_id, department_id, from_date, to_date, page } }),
  calendar: ({ start_date = '', end_date = '', employee_id = '', branch_id = '' } = {}) =>
    api.get('/shift-assignments/calendar', { params: { start_date, end_date, employee_id, branch_id } }),
  create: (data: any) => api.post('/shift-assignments', data),
  update: (id: number, data: any) => api.patch(`/shift-assignments/${id}`, data),
  delete: (id: number) => api.delete(`/shift-assignments/${id}`),
  // Bulk assign shifts to multiple employees and/or dates
  bulkAssign: (data: {
    shift_id: number;
    employee_ids: number[];
    dates?: string[];
    date_from?: string;
    date_to?: string;
    overwrite_existing?: boolean;
  }) => api.post('/shift-assignments/bulk', data),
};

export const shiftChangeRequestsAPI = {
  list: ({ page = 1, per_page = 15, status = '', change_type = '', branch_id = '', request_date = '' } = {}) =>
    api.get('/shift-change-requests', { params: { page, per_page, status, change_type, branch_id, request_date } }),
  get: (id: number) => api.get(`/shift-change-requests/${id}`),
  create: (data: any) => api.post('/shift-change-requests', data),
  approve: (id: number, data?: { resolution_notes?: string }) => api.post(`/shift-change-requests/${id}/approve`, data),
  reject: (id: number, data?: { resolution_notes?: string }) => api.post(`/shift-change-requests/${id}/reject`, data),
  cancel: (id: number, data?: { resolution_notes?: string }) => api.delete(`/shift-change-requests/${id}`, { data }),
};

// Attendance Events API
export const attendanceEventsAPI = {
  list: ({ employee_id = '', device_id = '', branch_id = '', date_from = '', date_to = '', page = 1, per_page = 20 } = {}) =>
    api.get('/attendance-events', { params: { employee_id, device_id, branch_id, date_from, date_to, page, per_page } }),
  get: (id: number) => api.get(`/attendance-events/${id}`),
  photo: (id: number) => api.get<Blob>(`/attendance-events/${id}/photo`, { responseType: 'blob' }),
  verify: (id: number, data: any) => api.post(`/attendance-events/${id}/verify`, data),
  delete: (id: number) => api.delete(`/attendance-events/${id}`),
};

// Penalty Rules API
export const penaltyRulesAPI = {
  list: ({ branch_id = '', page = 1 } = {}) =>
    api.get('/penalty-rules', { params: { branch_id, page } }),
  create: (data: any) => api.post('/penalty-rules', data),
  update: (id: number, data: any) => api.patch(`/penalty-rules/${id}`, data),
  delete: (id: number) => api.delete(`/penalty-rules/${id}`),
};

// Users API (Admin)
export const usersAPI = {
  list: ({ page = 1, search = '' } = {}) =>
    api.get('/users', { params: { page, search } }),
  get: (id: number) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: number, data: any) => api.patch(`/users/${id}`, data),
  delete: (id: number) => api.delete(`/users/${id}`),
  changePassword: (id: number, data: any) => api.post(`/users/${id}/password`, data),
};

// Roles API
export const rolesAPI = {
  list: () => api.get('/roles'),
};

// Organizations API
export const organizationsAPI = {
  list: ({ page = 1, per_page = 50, search = '', is_active = '' } = {}) =>
    api.get('/organizations', { params: { page, per_page, search, is_active } }),
  get: (id: number) => api.get(`/organizations/${id}`),
  create: (data: any) => api.post('/organizations', data),
  update: (id: number, data: any) => api.patch(`/organizations/${id}`, data),
  delete: (id: number) => api.delete(`/organizations/${id}`),
};

// Audit Logs API
export const auditLogsAPI = {
  list: ({ page = 1, user_id = '', action = '', model_type = '', model_id = '', date_from = '', date_to = '' } = {}) =>
    api.get('/audit-logs', { params: { page, user_id, action, model_type, model_id, date_from, date_to } }),
};

// Absence Rules API
export const absenceRulesAPI = {
  list: ({ branch_id = '' } = {}) =>
    api.get('/absence-rules', { params: { branch_id } }),
  update: (id: number, data: any) => api.patch(`/absence-rules/${id}`, data),
};

// Telegram Link API
export const telegramAPI = {
  startLinking: () => api.post('/telegram/link'),
  verifyCode: (data: any) => api.post('/telegram/verify', data),
  getLinkStatus: () => api.get('/telegram/link-status'),
  unlink: () => api.delete('/telegram/unlink'),
  updateSettings: (data: any) => api.patch('/telegram/notification-settings', data),
};

// Admin Telegram management API (manage_telegram permission)
export const adminTelegramAPI = {
  list: (params: {
    page?: number;
    per_page?: number;
    q?: string;
    branch_id?: number | '';
    department_id?: number | '';
    role?: string;
    verified?: boolean | '';
  } = {}) => api.get('/admin/telegram-links', { params }),
  stats: () => api.get('/admin/telegram-links/stats'),
  sendTest: (id: number, message?: string) =>
    api.post(`/admin/telegram-links/${id}/test`, { message }),
  unlink: (id: number) => api.delete(`/admin/telegram-links/${id}`),
  broadcast: (data: {
    message: string;
    target: 'all' | 'selected' | 'branch' | 'department' | 'role';
    link_ids?: number[];
    branch_id?: number;
    department_id?: number;
    role?: string;
  }) => api.post('/admin/telegram-links/broadcast', data),
};

// ===============================
// PAYROLL MODULE APIS
// ===============================

// Payroll Runs API
export const payrollRunsAPI = {
  list: ({ page = 1, per_page = 20, status = '', month = '' } = {}) =>
    api.get('/payroll/runs', { params: { page, per_page, status, month } }),
  get: (id: number) => api.get(`/payroll/runs/${id}`),
  create: (data: { branch_id: number; period_start: string; period_end: string; name?: string }) =>
    api.post('/payroll/runs', data),
  generate: (id: number) => api.post(`/payroll/runs/${id}/generate`),
  finalize: (id: number) => api.post(`/payroll/runs/${id}/finalize`),
  reopen: (id: number) => api.post(`/payroll/runs/${id}/reopen`),
  getPayslips: (id: number, params?: { page?: number; per_page?: number }) =>
    api.get(`/payroll/runs/${id}/payslips`, { params }),
  export: (id: number) =>
    api.get(`/payroll/runs/${id}/export`, { responseType: 'blob' }),
  delete: (id: number) => api.delete(`/payroll/runs/${id}`),
};

// Payroll Profiles API
export const payrollProfilesAPI = {
  list: ({ page = 1, per_page = 20, employee_id = '', branch_id = '' } = {}) =>
    api.get('/payroll/profiles', { params: { page, per_page, employee_id, branch_id } }),
  get: (id: number) => api.get(`/payroll/profiles/${id}`),
  create: (data: any) => api.post('/payroll/profiles', data),
  update: (id: number, data: any) => api.patch(`/payroll/profiles/${id}`, data),
  delete: (id: number) => api.delete(`/payroll/profiles/${id}`),
};

// Salary Components API
export const salaryComponentsAPI = {
  list: ({ page = 1, per_page = 20, type = '' } = {}) =>
    api.get('/payroll/components', { params: { page, per_page, type } }),
  get: (id: number) => api.get(`/payroll/components/${id}`),
  create: (data: any) => api.post('/payroll/components', data),
  update: (id: number, data: any) => api.patch(`/payroll/components/${id}`, data),
  delete: (id: number) => api.delete(`/payroll/components/${id}`),
};

// Employee Salary Components API
export const employeeComponentsAPI = {
  list: (employeeId: number) => api.get(`/employees/${employeeId}/components`),
  assign: (employeeId: number, data: { salary_component_id: number; amount: number }) =>
    api.post(`/employees/${employeeId}/components`, data),
  remove: (employeeId: number, componentId: number) =>
    api.delete(`/employees/${employeeId}/components/${componentId}`),
};

// Leave Types API
export const leaveTypesAPI = {
  list: () => api.get('/payroll/leave-types'),
  get: (id: number) => api.get(`/payroll/leave-types/${id}`),
  create: (data: any) => api.post('/payroll/leave-types', data),
  update: (id: number, data: any) => api.patch(`/payroll/leave-types/${id}`, data),
  delete: (id: number) => api.delete(`/payroll/leave-types/${id}`),
};

// Leave Requests API
export const leaveRequestsAPI = {
  list: ({ page = 1, per_page = 20, employee_id = '', status = '', from_date = '', to_date = '' } = {}) =>
    api.get('/leaves', { params: { page, per_page, employee_id, status, from_date, to_date } }),
  get: (id: number) => api.get(`/leaves/${id}`),
  create: (data: { employee_id: number; leave_type: string; date_from: string; date_to: string; reason?: string }) =>
    api.post('/leaves', data),
  update: (id: number, data: any) => api.patch(`/leaves/${id}`, data),
  delete: (id: number) => api.delete(`/leaves/${id}`),
  approve: (id: number, notes?: string) => api.post(`/leaves/${id}/approve`, { notes }),
  reject: (id: number, notes: string) => api.post(`/leaves/${id}/reject`, { rejection_reason: notes }),
};

// Payroll Rulesets API
export const payrollRulesetsAPI = {
  list: ({ branch_id = '' } = {}) =>
    api.get('/payroll/rulesets', { params: { branch_id } }),
  get: (id: number) => api.get(`/payroll/rulesets/${id}`),
  create: (data: any) => api.post('/payroll/rulesets', data),
  update: (id: number, data: any) => api.patch(`/payroll/rulesets/${id}`, data),
  delete: (id: number) => api.delete(`/payroll/rulesets/${id}`),
};

// Overtime Rules API
export const overtimeRulesAPI = {
  list: (params?: { ruleset_id?: number }) => api.get('/payroll/overtime-rules', { params }),
  create: (data: any) => api.post('/payroll/overtime-rules', data),
  update: (ruleId: number, data: any) =>
    api.patch(`/payroll/overtime-rules/${ruleId}`, data),
  delete: (ruleId: number) =>
    api.delete(`/payroll/overtime-rules/${ruleId}`),
};

// Payslips API
export const payslipsAPI = {
  get: (id: number) => api.get(`/payslips/${id}`),
  markPaid: (id: number, data?: { payment_date?: string; payment_method?: string; reference?: string; payment_reference?: string }) =>
    api.post(`/payslips/${id}/mark-paid`, data ? {
      ...data,
      payment_reference: data.payment_reference ?? data.reference,
    } : undefined),
  bulkMarkPaid: (runId: number, payslipIds: number[], data?: { payment_date?: string; payment_method?: string }) =>
    api.post(`/payroll/runs/${runId}/mark-paid`, { payslip_ids: payslipIds, ...data }),
};

// Payroll Adjustments API
export const payrollAdjustmentsAPI = {
  list: ({ payslip_id = '', page = 1 } = {}) =>
    api.get('/payroll/adjustments', { params: { payslip_id, page } }),
  get: (id: number) => api.get(`/payroll/adjustments/${id}`),
  create: (data: { payslip_id: number; type: 'earning' | 'deduction'; description: string; amount: number }) =>
    api.post('/payroll/adjustments', data),
  update: (id: number, data: any) => api.patch(`/payroll/adjustments/${id}`, data),
  delete: (id: number) => api.delete(`/payroll/adjustments/${id}`),
};

// Custom Hooks for React Query
export const useEmployees = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.employees(), params],
    queryFn: () => employeesAPI.list(params),
  });

export const useEmployee = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.employee(id),
    queryFn: () => employeesAPI.get(id),
    enabled: !!id,
  });

export const useDevices = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.devices(), params],
    queryFn: () => devicesAPI.list(params),
  });

export const useDevice = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.device(id),
    queryFn: () => devicesAPI.get(id),
    enabled: !!id,
  });

export const useTimesheets = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.timesheets(), params],
    queryFn: () => timesheetsAPI.list(params),
  });

export const useDashboard = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.dashboard(), params],
    queryFn: () => dashboardAPI.get(params),
  });

export const usePenalties = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.penalties(), params],
    queryFn: () => penaltiesAPI.list(params),
  });

export const useShifts = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.shifts(), params],
    queryFn: () => shiftsAPI.list(params),
  });

export const useDepartments = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.departments(), params],
    queryFn: () => departmentsAPI.list(params),
  });

export const useBranches = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.branches(), params],
    queryFn: () => branchesAPI.list(params),
  });

export const useHolidays = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.holidays(), params],
    queryFn: () => holidaysAPI.list(params),
  });

export const useShiftAssignments = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.shiftAssignments(), params],
    queryFn: () => shiftAssignmentsAPI.list(params),
  });

export const useShiftChangeRequests = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.shiftChangeRequests(), params],
    queryFn: () => shiftChangeRequestsAPI.list(params),
  });

export const useAttendanceEvents = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.attendanceEvents(), params],
    queryFn: () => attendanceEventsAPI.list(params),
  });

export const usePenaltyRules = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.penaltyRules(), params],
    queryFn: () => penaltyRulesAPI.list(params),
  });

export const useOperationsWeeklyPlan = (params: { week_start_date: string; branch_id?: string | number }, enabled = true) =>
  useQuery({
    queryKey: [...QUERY_KEYS.operationsWeeklyPlan(), params],
    queryFn: () => operationsAPI.weeklyPlan(params),
    enabled,
  });

export const useOperationsActiveStaff = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.operationsActiveStaff(), params],
    queryFn: () => operationsAPI.activeStaff(params),
  });

export const useEmployeeDayOffs = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.employeeDayOffs(), params],
    queryFn: () => employeeDayOffsAPI.list(params),
  });

export const usePenaltyPresets = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.penaltyPresets(), params],
    queryFn: () => penaltyPresetsAPI.list(params),
  });

export const useKpiSummary = (
  params: { date_from: string; date_to: string; branch_id?: string | number },
  enabled = true,
) =>
  useQuery({
    queryKey: [...QUERY_KEYS.kpiSummary(), params],
    queryFn: () => kpiAPI.summary(params),
    enabled,
  });

export const useUsers = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.users(), params],
    queryFn: () => usersAPI.list(params),
  });

export const useAuditLogs = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.auditLogs(), params],
    queryFn: () => auditLogsAPI.list(params),
  });

export const useRoles = () =>
  useQuery({
    queryKey: QUERY_KEYS.roles(),
    queryFn: () => rolesAPI.list(),
  });

// Mutations
export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => employeesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
    },
  });
};

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      employeesAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employee(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
    },
  });
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => employeesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employees() });
    },
  });
};

export const useCreateDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => devicesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.devices() });
    },
  });
};

export const useUpdateDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      devicesAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.device(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.devices() });
    },
  });
};

export const useApprovePenalty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => penaltiesAPI.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penalties() });
    },
  });
};

export const useSaveWeeklyPlan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      entries: Array<{ employee_id: number; date: string; shift_id?: number | null; is_day_off?: boolean; reason?: string }>;
    }) => operationsAPI.saveWeeklyPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.operationsWeeklyPlan() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.employeeDayOffs() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
    },
  });
};

export const useCreateShiftChangeRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => shiftChangeRequestsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftChangeRequests() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
    },
  });
};

export const useApproveShiftChangeRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { resolution_notes?: string } }) =>
      shiftChangeRequestsAPI.approve(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftChangeRequests() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
    },
  });
};

export const useRejectShiftChangeRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { resolution_notes?: string } }) =>
      shiftChangeRequestsAPI.reject(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftChangeRequests() });
    },
  });
};

export const useCancelShiftChangeRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { resolution_notes?: string } }) =>
      shiftChangeRequestsAPI.cancel(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftChangeRequests() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
    },
  });
};

export const useDeleteAttendanceEvent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => attendanceEventsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.attendanceEvents() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
    },
  });
};

export const useApplyChecklistPenalty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      employee_id: number;
      work_date: string;
      preset_ids: number[];
      preset_amounts?: Record<number, number>;
      notes?: string;
    }) =>
      penaltiesAPI.applyChecklist(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penalties() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.timesheets() });
    },
  });
};

export const useImportSales = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { records: any[]; default_bonus_rate?: number }) => kpiAPI.importSales(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.kpiSummary() });
    },
  });
};

export const useDeleteDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => devicesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.devices() });
    },
  });
};

// ===============================
// PAYROLL MODULE HOOKS
// ===============================

// Payroll Runs Hooks
export const usePayrollRuns = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.payrollRuns(), params],
    queryFn: () => payrollRunsAPI.list(params),
  });

export const usePayrollRun = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.payrollRun(id),
    queryFn: () => payrollRunsAPI.get(id),
    enabled: !!id,
  });

export const usePayslips = (runId: number, params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.payslips(runId), params],
    queryFn: () => payrollRunsAPI.getPayslips(runId, params),
    enabled: !!runId,
  });

export const usePayslip = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.payslip(id),
    queryFn: () => payslipsAPI.get(id),
    enabled: !!id,
  });

export const useCreatePayrollRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { branch_id: number; period_start: string; period_end: string; name?: string }) =>
      payrollRunsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRuns() });
    },
  });
};

export const useGeneratePayroll = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollRunsAPI.generate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRun(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payslips(id) });
    },
  });
};

export const useFinalizePayroll = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollRunsAPI.finalize(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRun(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRuns() });
    },
  });
};

export const useReopenPayroll = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollRunsAPI.reopen(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRun(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRuns() });
    },
  });
};

export const useDeletePayrollRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => payrollRunsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRuns() });
    },
  });
};

// Payroll Profiles Hooks
export const usePayrollProfiles = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.payrollProfiles(), params],
    queryFn: () => payrollProfilesAPI.list(params),
  });

export const usePayrollProfile = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.payrollProfile(id),
    queryFn: () => payrollProfilesAPI.get(id),
    enabled: !!id,
  });

export const useCreatePayrollProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => payrollProfilesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollProfiles() });
    },
  });
};

export const useUpdatePayrollProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      payrollProfilesAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollProfile(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollProfiles() });
    },
  });
};

// Salary Components Hooks
export const useSalaryComponents = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.salaryComponents(), params],
    queryFn: () => salaryComponentsAPI.list(params),
  });

export const useCreateSalaryComponent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => salaryComponentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.salaryComponents() });
    },
  });
};

// Leave Types Hooks
export const useLeaveTypes = () =>
  useQuery({
    queryKey: QUERY_KEYS.leaveTypes(),
    queryFn: () => leaveTypesAPI.list(),
  });

// Leave Requests Hooks
export const useLeaveRequests = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.leaveRequests(), params],
    queryFn: () => leaveRequestsAPI.list(params),
  });

export const useLeaveRequest = (id: number) =>
  useQuery({
    queryKey: QUERY_KEYS.leaveRequest(id),
    queryFn: () => leaveRequestsAPI.get(id),
    enabled: !!id,
  });

export const useCreateLeaveRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { employee_id: number; leave_type: string; date_from: string; date_to: string; reason?: string }) =>
      leaveRequestsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveRequests() });
    },
  });
};

export const useApproveLeaveRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes?: string }) =>
      leaveRequestsAPI.approve(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveRequests() });
    },
  });
};

export const useRejectLeaveRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: number; notes: string }) =>
      leaveRequestsAPI.reject(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveRequests() });
    },
  });
};

// Payroll Rulesets Hooks
export const usePayrollRulesets = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.payrollRulesets(), params],
    queryFn: () => payrollRulesetsAPI.list(params),
  });

export const useCreatePayrollRuleset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => payrollRulesetsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRulesets() });
    },
  });
};

// Payroll Adjustments Hooks
export const usePayrollAdjustments = (params?: any) =>
  useQuery({
    queryKey: [...QUERY_KEYS.payrollAdjustments(), params],
    queryFn: () => payrollAdjustmentsAPI.list(params),
  });

export const useCreatePayrollAdjustment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { payslip_id: number; type: 'earning' | 'deduction'; description: string; amount: number }) =>
      payrollAdjustmentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollAdjustments() });
    },
  });
};

export const useMarkPayslipPaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data?: { payment_date?: string; payment_method?: string; reference?: string } }) =>
      payslipsAPI.markPaid(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.payrollRuns() });
    },
  });
};

// ==============================
// Missing Mutation Hooks
// ==============================

export const useDeletePenalty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/penalties/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penalties() });
    },
  });
};

export const useCreatePenaltyPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => penaltyPresetsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyPresets() });
    },
  });
};

export const useUpdatePenaltyPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => penaltyPresetsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyPresets() });
    },
  });
};

export const useDeletePenaltyPreset = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => penaltyPresetsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyPresets() });
    },
  });
};

export const useCreatePenaltyRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => api.post('/penalty-rules', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyRules() });
    },
  });
};

export const useUpdatePenaltyRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.patch(`/penalty-rules/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyRules() });
    },
  });
};

export const useDeletePenaltyRule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/penalty-rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.penaltyRules() });
    },
  });
};

export const useCreateHoliday = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => holidaysAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.holidays() });
    },
  });
};

export const useUpdateHoliday = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => holidaysAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.holidays() });
    },
  });
};

export const useDeleteHoliday = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => holidaysAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.holidays() });
    },
  });
};

export const useCreateShiftAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => shiftAssignmentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
    },
  });
};

export const useUpdateShiftAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => shiftAssignmentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
    },
  });
};

export const useDeleteShiftAssignment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => shiftAssignmentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.shiftAssignments() });
    },
  });
};

export const useUpdateSalaryComponent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => salaryComponentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.salaryComponents() });
    },
  });
};

export const useDeleteSalaryComponent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => salaryComponentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.salaryComponents() });
    },
  });
};

export const useCreateBranch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => branchesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.branches() });
    },
  });
};

export const useUpdateBranch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => branchesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.branches() });
    },
  });
};

export const useDeleteBranch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => branchesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.branches() });
    },
  });
};

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => departmentsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments() });
    },
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => departmentsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments() });
    },
  });
};

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => departmentsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.departments() });
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => usersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users() });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => usersAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users() });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => usersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.users() });
    },
  });
};
